import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Bell, AlertTriangle } from 'lucide-react';
import { useAsync } from '../lib/useAsync';
import { store } from '../lib/store';
import { Eyebrow, SkeletonList, EmptyState } from '../components/ui';
import { dateLongIT } from '../lib/format';
import type { AlertItem } from '@shared/types';

export function ScadenzeScreen() {
  const navigate = useNavigate();
  const alerts = useAsync(() => store.alerts(), []);

  const items = alerts.data?.items ?? [];
  const critical = items.filter((a) => a.severity === 'critical');
  const upcoming = items.filter((a) => a.severity === 'upcoming');
  const within60 = alerts.data?.summary.within_60 ?? 0;

  const go = (a: AlertItem) =>
    navigate(a.kind === 'document' ? `/documenti/${a.id}` : `/garanzie/${a.id}`);

  return (
    <div className="screen">
      <div className="topbar">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Indietro">
          <ChevronLeft size={20} />
        </button>
        <div className="topbar__title">Scadenze</div>
        <span className="iconbtn" style={{ position: 'relative' }} aria-hidden>
          <Bell size={19} />
          {within60 > 0 && <span className="bell-dot" />}
        </span>
      </div>

      {/* Hero riepilogo */}
      <div className="alert" style={{ marginBottom: 22 }}>
        <AlertTriangle size={22} className="alert__icon" />
        <div>
          <div style={{ fontWeight: 700 }}>
            {within60} scadenz{within60 === 1 ? 'a' : 'e'} imminent{within60 === 1 ? 'e' : 'i'}
          </div>
          <div style={{ fontSize: 13 }}>nei prossimi 60 giorni</div>
        </div>
      </div>

      {alerts.loading ? (
        <SkeletonList rows={3} />
      ) : items.length === 0 ? (
        <EmptyState message="Nessuna scadenza nei prossimi 60 giorni." />
      ) : (
        <>
          {critical.length > 0 && (
            <>
              <Eyebrow>Critiche · entro 30 giorni</Eyebrow>
              <div className="list" style={{ marginTop: 11, marginBottom: 22 }}>
                {critical.map((a, i) => (
                  <AlertCard key={a.kind + a.id} alert={a} urgent={i === 0} onOpen={() => go(a)} />
                ))}
              </div>
            </>
          )}
          {upcoming.length > 0 && (
            <>
              <Eyebrow>In arrivo · 31–60 giorni</Eyebrow>
              <div className="list" style={{ marginTop: 11, marginBottom: 22 }}>
                {upcoming.map((a) => (
                  <AlertCard key={a.kind + a.id} alert={a} onOpen={() => go(a)} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Footer push */}
      <div className="alert" style={{ marginTop: 4 }}>
        <Bell size={20} className="alert__icon" />
        <div style={{ fontSize: 13 }}>
          <strong style={{ fontWeight: 600 }}>Notifiche push attive</strong>
          <br />
          Ti avvisiamo a 60, 30 e 7 giorni.
        </div>
      </div>
    </div>
  );
}

function AlertCard({ alert, urgent, onOpen }: { alert: AlertItem; urgent?: boolean; onOpen: () => void }) {
  return (
    <div className={`alertcard${urgent ? ' is-urgent' : ''}`}>
      <span className="alertcard__chip">
        <span className="alertcard__days">{alert.days_left}</span>
        <span className="alertcard__unit">giorni</span>
      </span>
      <div className="alertcard__body">
        <div className="alertcard__title">{alert.title}</div>
        <div className="alertcard__date">{dateLongIT(alert.expires_at)}</div>
      </div>
      <button type="button" className={`alertcard__cta${urgent ? '' : ' alertcard__cta--ghost'}`} onClick={onOpen}>
        {urgent ? 'Rinnova' : 'Apri'}
      </button>
    </div>
  );
}
